/**
 * PaperPop Shopify Theme - Global JavaScript
 */

// DOM Ready
function initializeTheme(root = document) {
  // Initialize all components
  initHeader(root);
  initScrollReveal(root);
  initImageReveal(root);
  initSpringHoverMotion(root);
  initAnnouncementBar(root);
  initProductCarousels(root);
}

document.addEventListener('DOMContentLoaded', function() {
  initializeTheme();
  initCartDrawer();
  initQuickAdd();
});

document.addEventListener('shopify:section:load', function(event) {
  initializeTheme(event.target);
});

/**
 * Header functionality
 */
function initHeader(root = document) {
  const header = root.matches?.('.header') ? root : root.querySelector('.header');
  if (!header) return;
  if (header.dataset.initialized === 'true') return;
  header.dataset.initialized = 'true';

  // Scroll behavior
  let lastScroll = 0;
  
  window.addEventListener('scroll', function() {
    const currentScroll = window.pageYOffset;
    
    if (currentScroll > 100) {
      header.classList.add('header--scrolled');
    } else {
      header.classList.remove('header--scrolled');
    }
    
    lastScroll = currentScroll;
  }, { passive: true });

  // Mobile menu toggle
  const menuToggle = header.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  
  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', function() {
      mobileMenu.classList.toggle('mobile-menu--open');
      document.body.classList.toggle('menu-open');
    });

    const overlay = mobileMenu.querySelector('[data-menu-overlay]');
    if (overlay) {
      overlay.addEventListener('click', function() {
        mobileMenu.classList.remove('mobile-menu--open');
        document.body.classList.remove('menu-open');
      });
    }
  }

  // Search toggle
  const searchToggle = header.querySelector('[data-search-toggle]');
  const searchContainer = header.querySelector('[data-search-container]');
  
  if (searchToggle && searchContainer) {
    searchToggle.addEventListener('click', function() {
      searchContainer.classList.toggle('header__search--open');
      if (searchContainer.classList.contains('header__search--open')) {
        searchContainer.querySelector('input').focus();
      }
    });
  }
}

/**
 * Scroll reveal animation
 */
function initScrollReveal(root = document) {
  const revealElements = root.querySelectorAll('.scroll-reveal:not([data-reveal-initialized])');
  
  if (!revealElements.length) return;
  if (!document.documentElement.classList.contains('animations-enabled')) {
    revealElements.forEach(el => el.classList.add('is-visible'));
    return;
  }
  
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, index * 50);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach((el, index) => {
      el.dataset.revealInitialized = 'true';
      const explicitIndex = Number.parseInt(el.dataset.index, 10);
      const staggerIndex = Number.isNaN(explicitIndex) ? index % 6 : explicitIndex % 6;
      el.style.setProperty('--reveal-delay', `${staggerIndex * 85}ms`);
      observer.observe(el);
    });
  } else {
    // Fallback for older browsers
    revealElements.forEach(el => el.classList.add('is-visible'));
  }
}

/**
 * Soft image loading transition
 */
function initImageReveal(root = document) {
  const images = root.querySelectorAll('img[loading="lazy"]:not([data-image-reveal])');

  images.forEach(image => {
    image.dataset.imageReveal = 'true';
    image.classList.add('image-reveal');

    const showImage = () => {
      requestAnimationFrame(() => image.classList.add('is-loaded'));
    };

    if (image.complete && image.naturalWidth > 0) {
      showImage();
    } else {
      image.addEventListener('load', showImage, { once: true });
      image.addEventListener('error', showImage, { once: true });
    }
  });
}

/**
 * Shared critically damped hover motion.
 * Every interactive component uses the same continuous acceleration profile.
 */
function initSpringHoverMotion(root = document) {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const motionConfigs = [
    {
      selector: '.product-card',
      values: {
        '--motion-art-y': [0, -7, 'px'],
        '--motion-art-scale': [1, 0.016, 'number'],
        '--motion-quick-y': [10, -10, 'px'],
        '--motion-quick-opacity': [0, 1, 'number']
      }
    },
    {
      selector: '.category-icons__item',
      values: {
        '--motion-root-y': [0, -4, 'px'],
        '--motion-icon-y': [0, -2, 'px'],
        '--motion-icon-scale': [1, 0.025, 'number']
      }
    },
    {
      selector: '.membership__benefit',
      values: {
        '--motion-root-y': [0, -3, 'px'],
        '--motion-icon-y': [0, -3, 'px'],
        '--motion-icon-scale': [1, 0.022, 'number']
      }
    },
    {
      selector: '.type-card',
      values: {
        '--motion-root-y': [0, -4, 'px'],
        '--motion-image-scale': [1, 0.018, 'number'],
        '--motion-content-y': [0, -3, 'px']
      }
    },
    {
      selector: '.feature-card',
      values: {
        '--motion-root-y': [0, -4, 'px'],
        '--motion-icon-y': [0, -4, 'px'],
        '--motion-icon-scale': [1, 0.03, 'number']
      }
    },
    {
      selector: '.custom-service__media',
      values: {
        '--motion-image-y': [0, -3, 'px'],
        '--motion-image-scale': [1, 0.008, 'number']
      }
    },
    {
      selector: '.membership__button, .hero__btn--primary, .btn-primary',
      values: {
        '--motion-root-y': [0, -2, 'px'],
        '--motion-root-scale': [1, 0.008, 'number']
      }
    },
    {
      selector: '.product-showcase__nav, .product-carousel__nav',
      values: {
        '--motion-control-scale': [1, 0.06, 'number']
      }
    },
    {
      selector: '.product-card__quick-add-btn',
      values: {
        '--motion-root-y': [0, -2, 'px'],
        '--motion-root-scale': [1, 0.035, 'number']
      }
    },
    {
      selector: '.card, .hover-lift',
      values: {
        '--motion-root-y': [0, -4, 'px'],
        '--motion-root-scale': [1, 0.008, 'number']
      }
    },
    {
      selector: '.hover-scale',
      values: {
        '--motion-root-scale': [1, 0.025, 'number']
      }
    },
    {
      selector: '.img-zoom',
      values: {
        '--motion-image-scale': [1, 0.04, 'number']
      }
    },
    {
      selector: '.header__action-btn, .header__menu-toggle, .footer__social-link, .announcement-bar__close, .cart-drawer__close, .cart-drawer__quantity-btn',
      values: {
        '--motion-root-y': [0, -1, 'px'],
        '--motion-root-scale': [1, 0.04, 'number']
      }
    },
    {
      selector: '.product-card__add-btn, .product-showcase__view-all, .hero__btn--outline, .btn-outline',
      values: {
        '--motion-root-y': [0, -1.5, 'px'],
        '--motion-root-scale': [1, 0.008, 'number']
      }
    }
  ];

  motionConfigs.forEach(config => {
    root.querySelectorAll(config.selector).forEach(element => {
      if (element.dataset.springMotion === 'true') return;
      element.dataset.springMotion = 'true';

      let position = 0;
      let velocity = 0;
      let target = 0;
      let animationFrame = null;
      let previousTime = 0;

      const render = () => {
        Object.entries(config.values).forEach(([property, [base, amount, unit]]) => {
          const currentValue = base + amount * position;
          const value = unit === 'number'
            ? currentValue.toFixed(5)
            : `${currentValue.toFixed(3)}${unit}`;
          element.style.setProperty(property, value);
        });
      };

      const step = currentTime => {
        if (!previousTime) previousTime = currentTime;
        const deltaTime = Math.min((currentTime - previousTime) / 1000, 0.032);
        previousTime = currentTime;

        const stiffness = 18;
        const damping = 2 * Math.sqrt(stiffness);
        const acceleration = stiffness * (target - position) - damping * velocity;

        velocity += acceleration * deltaTime;
        position += velocity * deltaTime;
        render();

        if (Math.abs(target - position) < 0.001 && Math.abs(velocity) < 0.001) {
          position = target;
          velocity = 0;
          previousTime = 0;
          render();
          animationFrame = null;
          return;
        }

        animationFrame = requestAnimationFrame(step);
      };

      const setTarget = nextTarget => {
        target = nextTarget;
        element.classList.toggle('is-motion-active', target === 1);

        if (animationFrame === null) {
          previousTime = 0;
          animationFrame = requestAnimationFrame(step);
        }
      };

      element.addEventListener('mouseenter', () => setTarget(1));
      element.addEventListener('mouseleave', () => setTarget(0));
      render();
    });
  });
}

/**
 * Announcement bar carousel
 */
function initAnnouncementBar(root = document) {
  const carousel = root.querySelector('[data-carousel]');
  if (!carousel) return;
  if (carousel.dataset.initialized === 'true') return;
  carousel.dataset.initialized = 'true';

  const slides = carousel.querySelectorAll('[data-slide]');
  const closeBtn = document.querySelector('[data-close]');
  const bar = document.querySelector('.announcement-bar');
  
  if (slides.length <= 1) return;

  let currentIndex = 0;
  const interval = 5000;

  function showSlide(index) {
    slides.forEach((slide, i) => {
      slide.classList.toggle('announcement-bar__slide--active', i === index);
    });
  }

  function nextSlide() {
    currentIndex = (currentIndex + 1) % slides.length;
    showSlide(currentIndex);
  }

  const timer = setInterval(nextSlide, interval);

  // Close button
  if (closeBtn && bar) {
    closeBtn.addEventListener('click', function() {
      clearInterval(timer);
      bar.style.display = 'none';
      sessionStorage.setItem('announcementClosed', 'true');
    });
  }

  // Check if previously closed
  if (sessionStorage.getItem('announcementClosed') === 'true' && bar) {
    bar.style.display = 'none';
  }
}

/**
 * Product carousels
 */
function initProductCarousels(root = document) {
  const carousels = root.querySelectorAll('[data-carousel-viewport]:not([data-initialized])');
  
  carousels.forEach(carousel => {
    carousel.dataset.initialized = 'true';
    const track = carousel.querySelector('[data-carousel-track]');
    const prevBtn = carousel.parentElement.querySelector('[data-carousel-prev]');
    const nextBtn = carousel.parentElement.querySelector('[data-carousel-next]');
    
    if (!track) return;

    let scrollAmount = 0;
    const slideWidth = 320; // Approximate slide width + gap

    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        scrollAmount = Math.max(0, scrollAmount - slideWidth);
        track.style.transform = `translateX(-${scrollAmount}px)`;
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        const maxScroll = track.scrollWidth - carousel.clientWidth;
        scrollAmount = Math.min(maxScroll, scrollAmount + slideWidth);
        track.style.transform = `translateX(-${scrollAmount}px)`;
      });
    }
  });
}

/**
 * Cart drawer functionality
 */
function initCartDrawer() {
  const cartToggle = document.querySelector('[data-cart-toggle]');
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  
  if (!cartToggle || !cartDrawer) return;

  const overlay = cartDrawer.querySelector('[data-cart-overlay]');
  const closeBtns = cartDrawer.querySelectorAll('[data-cart-close]');

  function openCart() {
    cartDrawer.classList.add('cart-drawer--open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
  }

  function closeCart() {
    cartDrawer.classList.remove('cart-drawer--open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
  }

  cartToggle.addEventListener('click', function(e) {
    e.preventDefault();
    openCart();
  });

  if (overlay) {
    overlay.addEventListener('click', closeCart);
  }

  closeBtns.forEach(btn => {
    btn.addEventListener('click', closeCart);
  });

}

function updateCartUI(cart) {
  // Update cart count
  const cartCount = document.querySelectorAll('[data-cart-count]');
  cartCount.forEach(el => {
    el.textContent = cart.item_count;
    el.classList.toggle('hidden', cart.item_count === 0);
  });

  // Update cart total
  const cartTotal = document.querySelector('[data-cart-total]');
  if (cartTotal) {
    cartTotal.textContent = formatMoney(cart.total_price);
  }
}

/**
 * Quick add to cart
 */
function initQuickAdd() {
  document.addEventListener('click', function(e) {
    const quickAddBtn = e.target.closest('[data-quick-add]');
    const addToCartBtn = e.target.closest('[data-add-to-cart]');
    
    const btn = quickAddBtn || addToCartBtn;
    if (!btn) return;

    e.preventDefault();
    
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Unable to add item to cart');
      return fetch(`${window.routes.cart_url}.js`);
    })
    .then(response => response.json())
    .then(async cart => {
      btn.classList.remove('is-loading');
      
      if (addToCartBtn) {
        addToCartBtn.classList.add('product-card__add-btn--added');
        addToCartBtn.textContent = window.cartStrings.added || 'Added!';
        
        setTimeout(() => {
          addToCartBtn.classList.remove('product-card__add-btn--added');
          addToCartBtn.textContent = window.cartStrings.addToCart || 'Add to Cart';
        }, 1500);
      }

      updateCartUI(cart);
      
      if (window.cartDrawer) {
        await window.cartDrawer.refreshDrawer();
        window.cartDrawer.open();
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      btn.classList.remove('is-loading');
    });
  });
}

/**
 * Format money
 */
function formatMoney(cents) {
  const format = window.Shopify?.money_format || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  return format.replace('{{amount}}', value);
}
